import React, { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { Bot, Play, Pause, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Live status card for the self-healing in-process bulk-enrichment worker.
 *
 * - Polls /api/admin/auto-enrich every 5s when the worker is enabled.
 * - One toggle button to start / stop. Survives backend restarts (the
 *   worker reads its own `enabled` flag from `db.system_flags`).
 * - Shows current phase, total processed this session, remaining queue,
 *   and an honest ETA based on a sustained 10 sites/min throughput.
 */
export default function AutoEnrichStatusCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data: res } = await api.get("/admin/auto-enrich");
      setData(res);
    } catch {
      /* swallow — backend may be busy */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [refresh]);

  const onToggle = async () => {
    if (!data) return;
    setToggling(true);
    try {
      const { data: res } = await api.post("/admin/auto-enrich/toggle", {
        enabled: !data.enabled,
      });
      setData(res);
      toast.success(res.enabled ? "Auto-enrich started" : "Auto-enrich paused");
    } catch {
      toast.error("Couldn't toggle — try again");
    } finally {
      setToggling(false);
    }
  };

  if (loading || !data) {
    return (
      <div className="border hairline-strong p-5 bg-white">
        <div className="flex items-center gap-2 text-sm mono text-[#666]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading auto-enrich status…
        </div>
      </div>
    );
  }

  const totalRemaining = (data.remaining_descriptions || 0) + (data.remaining_facilities || 0);
  const totalTarget = (data.total_campsites || 0) * 2; // descriptions + facilities
  const done = Math.max(0, totalTarget - totalRemaining);
  const pct = totalTarget > 0 ? Math.round(100 * done / totalTarget) : 0;
  const phaseLabel = {
    descriptions: "Writing descriptions",
    facilities: "Inferring facilities",
    done: "All done!",
    idle: data.enabled ? "Starting up…" : "Paused",
    error: "Recovering from error",
  }[data.phase] || "Unknown";

  return (
    <div className="border hairline-strong p-5 bg-white" data-testid="auto-enrich-status-card">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 border hairline ${data.enabled ? "bg-[#fff4ee]" : "bg-[#f5f5f5]"}`}>
            <Bot className={`w-5 h-5 ${data.enabled ? "text-[#ff4500]" : "text-[#888]"}`} strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <h2 className="font-display text-xl leading-tight">Auto-enrich worker</h2>
            <p className="text-[12px] mono text-[#666] mt-0.5">
              Self-healing background task. Survives backend restarts. Reuses Claude via Universal Key.
            </p>
          </div>
        </div>
        <button
          onClick={onToggle}
          disabled={toggling}
          className={`inline-flex items-center gap-1.5 text-[11px] mono uppercase tracking-wider px-3 py-2 border transition-colors flex-shrink-0 ${
            data.enabled
              ? "bg-white hover:bg-[#fff4ee] border-[#ff4500] text-[#ff4500]"
              : "bg-[#ff4500] hover:bg-[#e63d00] border-[#ff4500] text-white"
          } ${toggling ? "opacity-60" : ""}`}
          data-testid="auto-enrich-toggle"
        >
          {data.enabled ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
          {data.enabled ? "Pause" : "Start"}
        </button>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-[11px] mono mb-1">
          <span className="text-[#666]">{phaseLabel}</span>
          <span className="font-bold">{pct}%</span>
        </div>
        <div className="h-2 bg-[#f0f0f0] border hairline overflow-hidden">
          <div
            className={`h-full transition-all ${data.enabled ? "bg-[#ff4500]" : "bg-[#bbb]"}`}
            style={{ width: `${pct}%` }}
            data-testid="auto-enrich-progressbar"
          />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 mb-3">
        <div className="border hairline p-3">
          <div className="text-[10px] mono uppercase tracking-wider text-[#666]">Descriptions left</div>
          <div className="font-display text-2xl tracking-tight" data-testid="auto-enrich-remaining-desc">
            {(data.remaining_descriptions || 0).toLocaleString("en-GB")}
          </div>
        </div>
        <div className="border hairline p-3">
          <div className="text-[10px] mono uppercase tracking-wider text-[#666]">Facilities left</div>
          <div className="font-display text-2xl tracking-tight" data-testid="auto-enrich-remaining-fac">
            {(data.remaining_facilities || 0).toLocaleString("en-GB")}
          </div>
        </div>
        <div className="border hairline p-3">
          <div className="text-[10px] mono uppercase tracking-wider text-[#666]">Done this session</div>
          <div className="font-display text-2xl tracking-tight">
            {(data.total_processed_session || 0).toLocaleString("en-GB")}
          </div>
        </div>
        <div className="border hairline p-3">
          <div className="text-[10px] mono uppercase tracking-wider text-[#666]">ETA</div>
          <div className="font-display text-2xl tracking-tight">
            {data.eta_hours >= 1 ? `${data.eta_hours.toFixed(1)}h` : `${Math.round(data.eta_minutes)}m`}
          </div>
        </div>
      </div>

      {data.last_error && (
        <div className="flex items-start gap-2 p-3 bg-[#fff4ee] border border-[#ff4500] text-[12px] mono text-[#9a3412]">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="break-words" data-testid="auto-enrich-last-error">
            <strong>Last error:</strong> {data.last_error}
            <div className="text-[#5a3520] mt-1">Worker is auto-retrying with exponential backoff — usually self-recovers within minutes.</div>
          </div>
        </div>
      )}

      {data.phase === "done" && (
        <div className="flex items-center gap-2 p-3 bg-[#f3fbf4] border border-[#1f7a35] text-[12px] mono text-[#1f7a35]">
          <CheckCircle2 className="w-4 h-4" />
          <span><strong>All campsites enriched.</strong> Worker now re-checks hourly for newly-added sites.</span>
        </div>
      )}

      <div className="mt-3 text-[10px] mono text-[#888] flex items-center justify-between">
        <span>Batch {data.batch_size} × concurrency {data.concurrency}</span>
        {data.last_activity && (
          <span data-testid="auto-enrich-last-activity">
            Last activity {new Date(data.last_activity).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </div>
    </div>
  );
}
